// This are reducers to add dynamically to the store
import { createSelector, current } from "@reduxjs/toolkit";
import { getAreaBoxes, getAreaGrid, getAreaState, getCurrentValue2 } from "reduxModules/ducks/container";
import toolIndex from "toolConstants";
import { closest, findAngle, side } from "utilities/mathematic";
import { calculateCoordinates, getCollisions, getOverlaps, isInside } from "utilities/geometric";
import { clamp, isEqual, mapValues, max, merge, min, set, transform, values, indexOf, findKey, round, toPairs, pick, mapKeys, has, ceil, inRange, find, every, some, minBy, sortBy, trimEnd, omit, isEmpty, isNull, floor, compact, join, mergeWith, isNumber, keys } from "lodash";

import createCachedSelector from "re-reselect";
import { getDrawingBox, getIsMeter, getScale } from "./ui";
import { getStoreProp, pluralize } from "utilities/string";
import { distanceToString } from "utilities/format";

const roomSlice = {
  reducers: {
    /**
     * Sets the area orientation.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string} action.payload - The new area orientation ('lengthwise' or 'widthwise').
     */
    setAreaOrientation: (state, action) => {
      state.areas[state.activeTab].areaOrientation = action.payload
    },
    /**
     * Updates the door properties.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - The new door properties to be updated.
     */
    setDoor: (state, action) => {
      const area = state.areas[state.activeTab]
      const currentDoor = area.door;
      area.door = { ...currentDoor, ...action.payload }
    },
    /**
     * Sets the ceiling tiles properties.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - The new ceiling tiles properties.
     */
    setCeilingTiles: (state, action) => {
      const initTile = { ...toolIndex[state.currentTool].initialState.area(state).ceilingTiles };
      const payload = transform({ ...action.payload }, (result, value, key) => set(result, key.split('.'), value));
      const { tile, checked, qty, } = payload;
      const currentCeiling = tile ? { ...initTile, tile: tile } : state.areas[state.activeTab].ceilingTiles;
      if (checked) currentCeiling.checked = checked;
      if (qty && !isEqual(qty, currentCeiling.qty)) {
        currentCeiling.checked = [];
        payload.transform = { x: 0, y: 0 };
      };
      state.areas[state.activeTab].ceilingTiles = merge({}, { ...currentCeiling }, payload);

      const parameter = getStoreProp(action.type);
      toolIndex[state.currentTool].resetArea(state, parameter);
    },
    /**
     * Sets the table properties.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - The new table properties.
     */
    setTable: (state, action) => {
      const area = state.areas[state.activeTab]
      const initTable = { ...toolIndex[state.currentTool].initialState.area(state).table };
      let payload = transform({ ...action.payload }, (result, value, key) => set(result, key.split('.'), value));
      let { shape = null, size = {}, chair, isGhosted } = payload;
      let currentTable = area.table.shape === null && shape ? initTable : area.table;
      const defaultTable = toolIndex[state.currentTool].constants.tables[shape ?? currentTable.shape] ?? {};
      const tableMaximumSize = { ...toolIndex[state.currentTool].constants.tableMaximumSize };
      currentTable = shape ? {
        ...initTable,
        shape: shape,
        size: defaultTable.default(current(area.dimensions))
      } : has(payload, 'shape') && isNull(shape) ? initTable : currentTable;

      // ALWAYS validate when there's a table shape (not just when needsUpdate)
      if (currentTable.shape && currentTable.size) {
        const meetsMinimum = currentTable.size.length >= defaultTable.minimum.length &&
          currentTable.size.width >= defaultTable.minimum.width;
        if (!meetsMinimum) {
          payload.size = { length: 0, width: 0 };
        }
      };

      if (chair) {
        if (isGhosted && currentTable.ghostChairs.includes(chair)) {
          currentTable.ghostChairs.splice(currentTable.ghostChairs.indexOf(chair), 1)
        } else if (!isGhosted && !currentTable.ghostChairs.includes(chair)) {
          currentTable.ghostChairs.push(chair);
        }
        payload = omit(payload, ['chair', 'isGhosted'])
      };

      let emptyGhostCHairs = false;
      const needsUpdate = !isEmpty(size) && every(keys(size), key => (has(currentTable.size, key) && Math.abs(size[key] - currentTable.size[key]) > 0.001));
      if (currentTable.shape && needsUpdate) { //!isEmpty(size)        
        payload.transform = initTable.transform;
        emptyGhostCHairs = true;

        const roomBasedMaximum = mapValues(area.dimensions, x => x - 1.4);

        // Check if dimensions indicate a vertical table orientation
        const isVertical = (size.width ?? currentTable.size.width) > (size.length ?? currentTable.size.length);
        if (isVertical) [tableMaximumSize.length, tableMaximumSize.width] = [tableMaximumSize.width, tableMaximumSize.length];
        // set the size maximum limit
        const maximum = mergeWith({}, tableMaximumSize, roomBasedMaximum, (objValue, srcValue) => {
          if (isNumber(objValue) && isNumber(srcValue)) {
            return Math.min(objValue, srcValue);
          }
        });

        // Validate the table size
        switch (currentTable.shape) {

          case 'racetrack':
            payload.size = {
              length: size.length ? clamp(size.length, defaultTable.minimum.length, maximum.length) : currentTable.size.length,
              width: (size.width || size.length) ? clamp((size.width ?? currentTable.size.width), defaultTable.minimum.width, (size.length ?? currentTable.size.length)) : currentTable.size.width
            };
            break;
          case 'boat':
            const adjustedLength = size.width ? currentTable.size.length : max([defaultTable.minimum.length, size.length]);
            const adjustedWidth = size.length ? currentTable.size.width : max([defaultTable.minimum.width, size.width]);
            payload.size = {
              length: clamp(size.length ?? currentTable.size.length, defaultTable.minimum.length, maximum.length),
              width: clamp(adjustedLength > adjustedWidth * 5 ? adjustedLength * .2 : adjustedWidth, defaultTable.minimum.width, maximum.width)
            };
            break;
          case 'round':
            payload.size = {
              length: clamp(size.length ?? currentTable.size.length, defaultTable.minimum.length, min([max(values(tableMaximumSize)), ...values(roomBasedMaximum)])),
              width: clamp(size.length ?? currentTable.size.length, defaultTable.minimum.length, min([max(values(tableMaximumSize)), ...values(roomBasedMaximum)]))
            };
            break;
          case 'bullet':
            payload.size = {
              length: size.length ? clamp(size.length, currentTable.size.width / 2 + size.length * 0.03, maximum.length) : currentTable.size.length,
              width: size.width ? clamp(size.width, defaultTable.minimum.width, min([maximum.width, currentTable.size.length * 1.94])) : currentTable.size.width
            };
            break;
          case 'ushape':
          case 'vshape':
            payload.size = mapValues(size, (v, k) => clamp(v, defaultTable.minimum[k], roomBasedMaximum[k]));
            break;
          default:
            payload.size = mapValues(size, (v, k) => clamp(v, defaultTable.minimum[k], maximum[k]));
        };

        const meetsMinimum = every(keys(payload.size), prop => currentTable.size[prop] >= defaultTable.minimum[prop]);
        if (!meetsMinimum) {
          payload.size = { length: 0, width: 0 };
        };
      };

      const newTable = merge({}, { ...currentTable }, payload);
      newTable.ghostChairs = emptyGhostCHairs ? [] : currentTable.ghostChairs;
      area.table = newTable;

      const parameter = getStoreProp(action.type);
      toolIndex[state.currentTool].resetArea(state, parameter, action.payload);

    },
    /**
     * Sets the microphones properties.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - The new microphones properties.
     */
    setMicrophones: (state, action) => {
      const area = state.areas[state.activeTab];
      const currentMics = area.microphones;
      const newMic = { ...action.payload };
      const micChange = 'model' in newMic;
      const model = (micChange ? newMic : currentMics).model.replace('mic', '_mic');
      const defMic = toolIndex[state.currentTool].constants.equipment.microphones[model];
      newMic.height = micChange ? defMic.height(area) : clamp((newMic.height ?? currentMics.height), ...(defMic.heightRange(area)));
      newMic.coverage = micChange && !('coverage' in newMic) ? defMic.coverage(area) : (newMic.coverage ?? currentMics.coverage);
      if (has(newMic, 'coverageDensity.value')) newMic.qty = newMic.coverageDensity.value;
      newMic.coverageDensity = { ...currentMics.coverageDensity, ...newMic?.coverageDensity };
      area.microphones = { ...currentMics, ...newMic };

      const parameter = getStoreProp(action.type)
      toolIndex[state.currentTool].resetArea(state, parameter, action.payload)
    },
    /**
     * Sets the acoustics property.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string} action.payload - The new acoustics value.
     */
    setAcoustics: (state, action) => {
      state.areas[state.activeTab].acoustics = action.payload;
      const parameter = getStoreProp(action.type);
      toolIndex[state.currentTool].resetArea(state, parameter);
    },
    /**
     * Sets the speakers properties.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - The new speakers properties.
     */
    setSpeakers: (state, action) => {
      const area = state.areas[state.activeTab];
      const currentSpkrs = area.speakers;
      const newSpeaker = { ...action.payload };
      const spkrChange = 'model' in newSpeaker;
      const model = (spkrChange ? newSpeaker : currentSpkrs).model;
      const defSpkr = toolIndex[state.currentTool].constants.equipment.speakers[model];
      newSpeaker.coverage = spkrChange ? defSpkr.coverage(area) : (newSpeaker.coverage ?? currentSpkrs.coverage);
      state.areas[state.activeTab].speakers = { ...currentSpkrs, ...newSpeaker }

      const parameter = getStoreProp(action.type)
      toolIndex[state.currentTool].resetArea(state, parameter, action.payload)
    },
    /**
     * Updates properties for a specific device (microphones, speakers, etc.).
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - An object with device names as keys and their new properties as values.
     */
    updateDevice: (state, action) => {
      const area = state.areas[state.activeTab];
      toPairs(action.payload).forEach(([device, properties]) => {
        const currentDevice = area[device];
        if ('layout' in properties) {
          const newLayout = values(properties.layout).map((device, index) => ({ ...currentDevice.layout[index], ...device }));
          properties.layout = newLayout;
          properties.modelName = newLayout[0]?.modelName
        };
        area[device] = { ...currentDevice, ...properties }
      });
    },

    /**
     * Adds a new device to the current area (microphones, speakers, etc.).
     * @param {Object} state - The current state of the room.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - The payload of the action
     * @param {string} action.payload.device - The type of device being added (e.g., 'speakers', 'microphones')
     * @param {Object} action.payload.location - The location coordinates for the new device
     * 
     * @description
     * This function adds a new device to the layout of the currently active area. It performs the following actions:
     * 1. Retrieves the current area based on the active tab.
     * 2. Extracts the device type and location from the action payload.
     * 3. Gets the current model for the specified device type.
     * 4. Adds a new device object to the layout array of the specified device type.
     * 5. Increments the quantity count for the device type.
     * 
     * @example
     * // Dispatch an action to add a new microphone
     * dispatch(addDevice({
     *   device: 'microphones',
     *   location: { x: 10, y: 20, z: 3 }
     * }));
     * 
     */
    addDevice: (state, action) => {
      const area = state.areas[state.activeTab];
      const { device, location } = action.payload;
      const { model, modelName} = area[device];
      area[device].layout.push({
        model,
        modelName,
        location
      })
      area[device].qty += 1;
    }, 

    /**
     * Removes a specific device from the current active area in the room.
     * @param {Object} state - The current state of the room.
     * @param {Object} action - The Redux action object
     * @param {Object} action.payload - The payload of the action
     * @param {string} action.payload.device - The type of device to remove (e.g., 'speakers', 'microphones')
     * @param {number} action.payload.id - The index of the device to remove from the layout array
     * 
     * @example
     * // Dispatch an action to remove a device
     * dispatch(removeDevice({ device: 'speakers', id: 2 }));
     * 
     * @description
     * This reducer function removes a specific device from the layout of the currently active area.
     * It updates both the layout array and the quantity count for the specified device type.
     * The function assumes that the device type exists in the area object and has a 'layout' array
     * and a 'qty' property. It's typically used in response to user actions like clicking to remove
     * a device in the room designer interface.
     * 
     * @note This function mutates the state directly, which is the recommended approach
     * when using Redux Toolkit's createSlice.
     */
    removeDevice: (state, action) => {
      const area = state.areas[state.activeTab];
      const { device, id } = action.payload;
      area[device].layout.splice(id, 1);
      area[device].qty -= 1;
    },

    /**
     * Sets the cameras properties.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {Object} action.payload - The new cameras properties.
     */
    setCameras: (state, action) => {
      const initialCameras = { ...toolIndex[state.currentTool].initialState.area(state).cameras };
      const newCamera = { ...action.payload };
      if (!newCamera.hasCamera) {
        state.areas[state.activeTab].cameras = {
          hasCamera: false,
          coverageRadius: 0,
          model: '',
          layout: [],
          qty: 0
        };
        state.areas[state.activeTab].customized.cameras = false;
      } else {
        state.areas[state.activeTab].cameras = { ...initialCameras }
      }
    },

    /**
     * Sets the product family.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string} action.payload - The new product family.
     */
    setProductFamily: (state, action) => {
      state.areas[state.activeTab].productFamily = action.payload
    },

    /**
     * Sets the equipment list.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string} action.payload - The new equipment list.
     */
    setEquipmentList: (state, action) => {
      state.areas[state.activeTab].equipmentList = action.payload
    },

    /**
     * Sets the hasBar property.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string} action.payload - hasbar property.
     */
    setHasBar: (state, action) => {
      state.areas[state.activeTab].hasBar = action.payload
    },

    /**
     * Resets area after the tutorial closes.
     * @param {Object} state - The current state.
     * @param {Object} action - The action object.
     * @param {string} action.payload - parameters to reset.
     */
    resetTutorialChanges: (state, action) => {
      if (action.payload) {
        state.areas[state.activeTab] = { ...state.areas[state.activeTab], ...action.payload }
      }
    },

    setCollisions: (state, action) => {
      state.areas[state.activeTab].collisions = action.payload;
    }

  },
}


export default roomSlice;

// fixme this doesn't need to export - exporting just for testing in Devices
/**
 * Selector to get coverage boxes for the current area.
 * @function
 * @param {Object} state - The Redux state.
 * @returns {Object} An object containing coverage boxes for 'full_room' and 'focused' scenarios.
 */
export const getCoverageBoxes = createSelector(
  state => getAreaState(state),
  state => toolIndex[state.container.currentTool].constants,
  (area, constants) => {
    const { dimensions, location, table } = area;
    const { area: { table2wall }, tables } = constants;
    const endTable = tables[table?.shape]?.endTable ?? false;
    return {
      full_room: {
        length: dimensions.length,
        width: dimensions.width,
        left: location.left,
        top: location.top,
        spkrAdj: 0
      },
      focused: {
        length: table.shape ? table.size.length + table2wall * (endTable ? 1 : 2) : dimensions.length,
        width: table.shape ? table.size.width + table2wall * 2 : dimensions.width,
        left: location.left + table.shape ? (dimensions.length - table.size.length) * .5 - table2wall * (endTable ? 0 : 1) : 0,
        top: location.top + table.shape ? (dimensions.width - table.size.width) * .5 - table2wall : 0,
        spkrAdj: table.shape ? 0 : 1
      }
    }
  }
);

/**
 * Creates a coordinate transformer function based on the current area and table transform.
 * @function
 * @param {Object} state - The Redux state.
 * @returns {Function} A function that transforms coordinates based on the current area and table position.
 */
const createCoordinateTransformer = createSelector(
  state => getAreaBoxes(state),
  state => getCurrentValue2(state, "table.transform"),
  (areaBoxes, tableTransform) => {
    const areaBox = areaBoxes[0];
    const angle = tableTransform.a * Math.PI / 180;
    const cosAngle = Math.cos(angle);
    const sinAngle = Math.sin(angle);

    const transformPoint = ({ x, y }) => {
      const dx = x - areaBox.cx;
      const dy = y - areaBox.cy;
      return {
        x: areaBox.cx + (dx * cosAngle - dy * sinAngle) + tableTransform.x,
        y: areaBox.cy + (dx * sinAngle + dy * cosAngle) + tableTransform.y
      };
    };
    return transformPoint;
  }
);


/**
* @function createDeviceAdjutmentAndAim
* @description Creates a function that calculates device position adjustments and aim points.
* 
* @param {Object} state - Redux state
* @returns {Function} Device adjustment function
* 
* @callback adjustAndAim
* @param {string} deviceType - Type of device ('cameras', 'speakers', 'microphones')
* @param {Object} device - Device object with location and model information
* @param {boolean} [render=false] - Whether adjustment is for rendering purposes
* @returns {Object} Adjusted device data
* @property {string} model - Device model identifier
* @property {string} modelName - Full model name
* @property {Object} location - Adjusted device location
* @property {number} location.x - Adjusted x coordinate
* @property {number} location.y - Adjusted y coordinate
* @property {Object} aimPoint - Point where device should aim
* @property {number} [horAngle] - Horizontal angle for cameras (0-360°)
* 
* @description
* Creates a function that:
* 1. Adjusts device positions based on type and mounting location:
*    - Bar-mounted devices get wall offset adjustments
*    - Positions are clamped to valid room coordinates
* 2. Calculates aim points:
*    - Cameras aim at table center if present, room center otherwise
*    - Bar devices aim based on their wall position
* 3. Computes angles:
*    - Camera horizontal angles based on aim point
*    - Angles normalized to 0-360° range
* 
* @example
* const adjustDevice = createDeviceAdjutmentAndAim(state);
* const adjusted = adjustDevice('cameras', {
*   model: 'Camera1',
*   modelName: 'PTZ Camera',
*   location: { x: 10, y: 20 }
* });
* // Returns {
* //   model: 'Camera1',
* //   modelName: 'PTZ Camera',
* //   location: { x: 10, y: 20 },
* //   aimPoint: { x: 15, y: 25 },
* //   horAngle: 45
* // }
* 
* @note
* - Bar fence offset is fixed at 0.134 units
* - Render mode skips position adjustments but keeps aim calculations
* - Uses room diagonal for determining wall positions
*/
export const createDeviceAdjutmentAndAim = createSelector(
  getDrawingBox,
  state => getCurrentValue2(state, 'table'),
  state => toolIndex[state.container.currentTool].constants.equipment,
  //(state, deviceType, device, render) => ({ deviceType, device, render: render ?? false }),
  (drawingBox, table, equipment) => {
    const { bbox, scale } = drawingBox;
    const { x0, y0, cx, cy } = bbox;
    const { shape, transform: tableCenter } = table;
    const hasTable = Boolean(shape);
    const barFence = .134;

    const adjustAndAim = (deviceType, device, render = false) => {
      const { model, modelName, location } = device;
      const isCamera = deviceType === 'cameras';
      const adjustment = { x: 0, y: 0 };
      const { isBarMic = false, isBarSpkr = false, isBarCamera = false } = equipment?.[deviceType]?.[model] ?? {};
      // Calculate djustment 
      let aimPoint;
      if (isBarMic || isBarSpkr || isCamera) {
        const { x, y } = mapValues(location, (value, key) => value * scale + bbox[`${key}0`]);
        aimPoint = { ...location };

        // Calculate the angle of the device in degrees
        let angle = Math.atan2(y - cy, x - cx) * (180 / Math.PI) + 180;
        angle = angle % 360;

        // Calculate the room diagonal angle
        const roomDiagonal = Math.atan2(y0 - cy, x0 - cx) * (180 / Math.PI) + 180;
        // Determine barSide and calculate adjustments
        if (angle <= roomDiagonal) { // left side
          adjustment.x = -barFence;
          aimPoint.x += barFence;
        } else if (angle <= 180 - roomDiagonal) { // top side
          adjustment.y = -barFence;
          aimPoint.y += barFence;
        } else if (angle <= 180 + roomDiagonal) { // right side
          adjustment.x = barFence;
          aimPoint.x -= barFence;
        } else if (angle <= 360 - roomDiagonal) { // bottom side
          adjustment.y = barFence;
          aimPoint.y -= barFence;
        } else { // left side (wrap aroound)
          adjustment.x = -barFence;
          aimPoint.x += barFence;
        };
      };
      const adjustedLocation = {
        x: Math.max(0, (location.x + (render ? 0 : adjustment.x))),
        y: Math.max(0, (location.y + (render ? 0 : adjustment.y))),
        ...omit(location, ['x', 'y'])
      };

      let horAngle;
      if (isCamera && !isBarCamera) {
        aimPoint.x = (cx - x0) / scale + (hasTable ? tableCenter.x : 0);
        aimPoint.y = (cy - y0) / scale + (hasTable ? tableCenter.y : 0);
        horAngle = Math.atan2((adjustedLocation.y - aimPoint.y), (aimPoint.x - adjustedLocation.x)) * (180 / Math.PI);
        horAngle = round((360 - horAngle) % 360);
      };
      return {
        model,
        modelName,
        location: adjustedLocation,
        aimPoint,
        ...isFinite(horAngle) && { horAngle }
      };
    };
    return adjustAndAim
  }
);



/**
* @function getDeviceLayouts
* @description Creates comprehensive device layouts including microphones, speakers, and cameras with coverage calculations.
* 
* @param {Object} state - Redux state
* @returns {Object} Complete device layout information
* @property {Object} microphones - Microphone layout and coverage data
* @property {Object} speakers - Speaker layout and coverage data
* @property {Object} cameras - Camera layout and coverage data
* 
* @description
* This selector orchestrates device placement by:
* 1. Calculating microphone placement and coverage:
*    - Handles ceiling and table microphones
*    - Considers coverage boxes and overlap
*    - Centers devices in ceiling tiles when applicable
* 
* 2. Determining speaker placement:
*    - Uses microphone positions to inform placement
*    - Calculates coverage angles and radius
*    - Adjusts for ceiling tile alignment
* 
* 3. Computing camera positions:
*    - Determines mounting locations
*    - Calculates coverage areas
* 
* 4. Generating plot data for visualization
* 
* @example
* const layouts = getDeviceLayouts(state);
* // Returns {
* //   microphones: {
* //     micDistanceLegendMax: 20,
* //     micMaxRadius: 10,
* //     coverageDensity: {...},
* //     layout: [...],
* //     qty: 4,
* //     plotData: {...}
* //   },
* //   speakers: {...},
* //   cameras: {...}
* // }
* 
* @note
* - Respects customized layouts (doesn't recalculate if customized)
* - Handles device-tile centering with error tracking
* - Transforms coordinates based on table position for focused coverage
* - Uses device-specific grid spacing for plot calculations
*/
export const getDeviceLayouts = createSelector(
  state => getAreaState(state),
  getCoverageBoxes,
  createCoordinateTransformer,
  state => getCenters(state),
  state => getScale(state),
  state => getIsMeter(state),
  state => (space = 1) => getAreaGrid(state, space),
  createDeviceAdjutmentAndAim,
  state => toolIndex[state.container.currentTool].constants,

  (area, coverageBoxes, transformer, centers, scale, isMeter, grid, adjustAndAim, constants) => {
    const { microphones, speakers, cameras, table, customized, ceilingTiles: { tile } } = area;
    const { transform: tableTransform = { x: 0, y: 0, a: 0 } } = table;
    const { microphones: isMicCustom, speakers: isSpkrCustom, cameras: isCameraCustom } = customized;
    const hasTiles = tile !== "no_ceiling_tiles";
    const { equipment, acoustics, area: { talkerHeight }, tables } = constants;
    const isEndTable = tables[table.shape]?.endTable ?? false;

    // Calculate microphone layout
    if (isEmpty(microphones)) return
    const micModel = microphones.model.replace('mic', '_mic');
    const micMaxDist = equipment.microphones[micModel].micData[indexOf(acoustics, area.acoustics)];
    const { isCeilingMic = false, isTableMic = false } = equipment.microphones[micModel]
    const micRadius = round(side(micMaxDist, (microphones.height - talkerHeight)), 4);
    const micCoverageBox = coverageBoxes[findKey(microphones.coverage)];
    const micOverlaps = getOverlaps(micCoverageBox, micRadius, microphones.coverage, table, isEndTable);
    // Calculate initial microphone placement
    let { coords: micCoords, qty: micQty, updatedJumpPoints = null } = calculateMicPlacement(area, micOverlaps, micCoverageBox, micRadius, scale, constants);
    // Translate and/or Rotate microphone coordinates to align with the center of the table if microphone coverage is set to focused and table has been moved
    const isMicFocused = microphones.coverage.focused;
    const isTableTransformed = some(tableTransform);

    if ((isMicFocused || isTableMic) && isTableTransformed) {
      micCoords = transformCoordinates(micCoords, transformer, 'location');
      // Filter mics that are outside the room
      micCoords = micCoords.filter(mic => isInside(values(mic.location), coverageBoxes['full_room']));
    };

    // If there are ceiling tiles, microphone is in the ceiling and have not been customized, attempt to center in tiles 
    let copyOfCenters = centers ? centers.map(center => ({ ...center })) : null;
    let micPlacementError = 0
    if (hasTiles && isCeilingMic && !isMicCustom && copyOfCenters) {
      for (const mic of micCoords) {
        if (!every(mic.location, objValue => objValue)) continue
        // Get  the target tile to place the microphone
        const finalTile = findClosestTile(copyOfCenters, mic.location);
        // If a tile was found, center mic in tile and block adjacent 
        if (finalTile) {
          mic.location = { x: finalTile.x, y: finalTile.y };
          copyOfCenters = blockTiles(copyOfCenters, finalTile);
        } else {
          // If not solution is found, leave mic whefe it is and increase error count
          micPlacementError++;
        }
      };
    };

    // if (micPlacementError > 0) console.log("Couldn't center: ", micPlacementError, " microphones");

    // Calculate speaker layout based on microphone placement
    const spkrModel = speakers.model;
    const { coverageAngle, isCeilingSpkr = false } = equipment.speakers[spkrModel];
    const speakerHeight = area.ceilingHeight.min;
    const spkrRadius = round((speakerHeight - talkerHeight) * Math.tan(coverageAngle * Math.PI / 180), 4);
    const spkrCoverageBox = coverageBoxes[findKey(speakers.coverage)];
    const spkrOverlaps = getOverlaps(spkrCoverageBox, spkrRadius, speakers.coverage)
    const spkrOverlap = spkrOverlaps.overlaps[0];

    // Calculate speaker placement considering microphone placement
    let spkrCoords = calculateSpeakerPlacement(area, spkrOverlap, spkrCoverageBox, spkrRadius, micQty, scale, constants); //  spkrCoverageBox, spkrRadius, spkrOverlaps, micPlacement);

    // Translate / Rotate speakers coordinates to align with the center of the table if speaker coverage is set to focused and table has been moved
    const isSpkrFocused = speakers.coverage.focused;
    if (isSpkrFocused && isTableTransformed) {
      spkrCoords = transformCoordinates(spkrCoords, transformer, 'location');
      // Filter spkrs that are outside the room
      spkrCoords = spkrCoords.filter(spkr => isInside(values(spkr.location), coverageBoxes['full_room']));
    };
    // If there are ceiling tiles and speakers have not been customized, attempt to center in tiles 
    let spkrPlacementError = 0
    if (hasTiles && isCeilingSpkr && !isSpkrCustom && copyOfCenters) {
      for (const spkr of spkrCoords) {
        if (!every(spkr.location, objValue => objValue)) continue
        // Get  the target tile to place the speaker
        const finalTile = findClosestTile(copyOfCenters, spkr.location);
        // If a tile was found, center speaker 
        if (finalTile) {
          spkr.location = { x: finalTile.x, y: finalTile.y };
        } else if (isCeilingSpkr) {
          // If not solution is found, leave speaker where it is and increase error count
          spkrPlacementError++;
        }
      };
    };

    // if (spkrPlacementError > 0) console.log("Couldn't center: ", spkrPlacementError, " speakers");

    // Find collisions between microphones and speakers abnd fix only if devices have not been customized
    let collisions, centerPoints, movedDevices = 0;
    if (isCeilingMic && !isMicCustom && !isSpkrCustom) {
      // Initial collision check
      ({ collisions, centerPoints } = getCollisions((isMicCustom ? microphones.layout : micCoords), (isSpkrCustom ? speakers.layout : spkrCoords)));

      if (collisions > 0 && !hasTiles) {
        copyOfCenters = centers ? centers.map(center => ({ ...center })) : null;
        for (let mic of micCoords) {
          if (some(centerPoints.microphones, point => isEqual(point, mic.location))) {
            const finalTile = findClosestTile(copyOfCenters, mic.location);
            if (finalTile) {
              mic.location = { x: finalTile.x, y: finalTile.y };
              copyOfCenters = blockTiles(copyOfCenters, finalTile);
              movedDevices++;
            };
          };
        };

        for (let spkr of spkrCoords) {
          if (some(centerPoints.speakers, point => isEqual(point, spkr.location))) {
            const finalTile = findClosestTile(copyOfCenters, spkr.location);
            if (finalTile) {
              spkr.location = { x: finalTile.x, y: finalTile.y };
              copyOfCenters = blockTiles(copyOfCenters, finalTile);
              movedDevices++;
            };
          };
        };
        ({ collisions, centerPoints } = getCollisions(micCoords, spkrCoords));
      };
    } else if (isCeilingMic) {
      // Just check for collisions 
      ({ collisions, centerPoints } = getCollisions(microphones.layout, speakers.layout));
    };

    // set the errors if any
    // Helper function to format device count and name
    const formatDeviceText = (count, singularName) => {
      const numberWords = {
        1: 'one',
        2: 'two',
        3: 'three'
      };
      return count > 0 ? `${numberWords[count] || count} ${pluralize(count, singularName)}` : '';
    };

    const errors = [];
    const hasMicHeightError = microphones.height > microphones.maxHeight;

    if (hasMicHeightError) {
      errors.push({
        code: 1110,
        msg: `Microphone is too high to pick up any talkers at given room acoustics.\nAdjusting Pendant microphone height to ${distanceToString(microphones.maxHeight, isMeter)}.`,
        reset: true
      }, { code: 1200 }, { code: 1300 }, { code: 1400 })
    } else {
      // Handle error 1200 - device centering issues
      if (micPlacementError || spkrPlacementError) {
        const deviceTexts = compact([
          formatDeviceText(micPlacementError, 'microphone'),
          formatDeviceText(spkrPlacementError, 'speaker')
        ]);

        errors.push({
          code: 1200,
          msg: `Could not center ${join(deviceTexts, ' and ')} to ceiling tiles.`,
          reset: true
        })
      } else {
        errors.push({ code: 1200 })
      };

      // Handle collisions (1300) and moved devices (1400) together
      const hasCollisions = collisions > 0;
      const hasMovedDevices = movedDevices > 0;

      if (hasCollisions && hasMovedDevices) {
        // Combined message for both collisions and moved devices
        errors.push({
          code: 1300,
          msg: `Devices were moved to improve performance, but ${formatDeviceText(collisions, 'pair')} of microphones and speakers (highlighted) are still closer than recommended. Consider moving or deleting devices manually.`,
          reset: true
        });
        // Add empty code for 1400 since we've handled both issues with the 1300 message
        errors.push({ code: 1400 });
      } else {
        // Handle individual cases
        if (hasCollisions) {
          errors.push({
            code: 1300,
            msg: isMicCustom || isSpkrCustom ?
              `Distance between ${formatDeviceText(collisions, 'pair')} of microphones and speakers (highlighted) is closer than recommended and may impact system performance. Consider moving or deleting devices manually.` :
              `Could not find a solution with proper distance between ${formatDeviceText(collisions, 'pair')} of microphones and speakers (highlighted). Consider moving or deleting devices manually.`,
            reset: true
          });
        } else {
          errors.push({ code: 1300 });
        }

        if (hasMovedDevices) {
          errors.push({
            code: 1400,
            msg: 'Microphone and speaker positions were adjusted to maintain proper distance between them for optimum performance. This may have caused some device misalignments. Devices can be moved if required.',
            reset: true
          })
        } else {
          errors.push({ code: 1400 })
        }
      }


    }



    // Calculate camera layout
    let cameraCoords = calculateCameraPlacement(area, constants);
    return {
      // centers: copyOfCenters, // to display centers on ceiling uncomment this line and check ;ines to uncomment in ceiling
      microphones: {
        micDistanceLegendMax: ceil(micMaxDist * 2),
        micMaxRadius: micRadius,
        coverageDensity: {
          value: micCoords.length,
          jumpPoints: updatedJumpPoints ?? micOverlaps.jumpPoints,
          overlaps: micOverlaps.overlaps,
        },
        layout: isMicCustom ? microphones.layout : micCoords,
        qty: isMicCustom ? microphones.qty : micCoords.length,
        plotData: calculateMicPlotData(area.id, isMicCustom ? microphones.layout : micCoords, grid()),
      },
      speakers: {
        coverageDensity: {
          value: spkrCoords.length,
          jumpPoints: spkrOverlaps.jumpPoints,
          overlaps: spkrOverlaps.overlaps
        },
        layout: isSpkrCustom ? speakers.layout : spkrCoords,
        qty: isSpkrCustom ? speakers.qty : spkrCoords.length,
        plotData: isCeilingSpkr ?
          calculagteCeilingRelativePloptData(area, isSpkrCustom ? speakers.layout : spkrCoords, grid(), constants) :
          calculateBarRelativePlotData(area, isSpkrCustom ? speakers.layout : spkrCoords, grid(.1), adjustAndAim, constants),
      },
      cameras: {
        layout: isCameraCustom ? cameras.layout : cameraCoords,
        qty: isCameraCustom ? cameras.qty : cameraCoords.length,
        plotData: calculateCameraPlotData(area.id, isCameraCustom ? cameras.layout : cameraCoords, grid(), adjustAndAim)
      },
      collisions: centerPoints,
      errors: errors,
    };
  }
);

/**
 * Selector to get tile quantities for the ceiling.
 * @function
 * @param {Object} state - The Redux state.
 * @returns {Object} An object containing actual, draw, and maximum tile quantities for x and y directions.
 */
export const getTileQuantities = createSelector(
  state => getCurrentValue2(state, 'dimensions'),
  state => getCurrentValue2(state, 'ceilingTiles'),
  state => toolIndex[state.container.currentTool].constants.ceilingTiles,
  (area, tiles, constants) => {
    const { length, width } = area;
    const { tile, qty } = tiles;

    // Set tile size depending on selection. If no tile is selected, use 2x2 for ref grid
    const hasTiles = tile !== "no_ceiling_tiles";
    const tileSize = constants[hasTiles ? tile : "2'_x_2'"];

    // Calculate the maximum number of tiles that fit in the room 
    const maxX = ceil(length / tileSize.x);
    const maxY = ceil(width / tileSize.y);

    // Check that qty is within max
    let { x = 0, y = 0 } = qty;
    x = x ? clamp(x, Math.min(1, maxX), maxX) : hasTiles ? maxX : 0;
    y = y ? clamp(y, Math.min(1, maxY), maxY) : hasTiles ? maxY : 0;

    // Set draw qunatitities. If using full tile coverage, adds 1 tile to the perimeter to the grid can be moved
    const drawX = x + (x && x === maxX ? 2 : 0);
    const drawY = y + (y && y === maxY ? 2 : 0);

    return {
      actualTileQty: {
        x: x,
        y: y
      },
      drawTileQty: {
        x: drawX,
        y: drawY
      },
      maxTileQty: {
        x: maxX + 2,
        y: maxY + 2
      }
    }
  }
);

/**
 * Selector to get the centers of ceiling tiles.
 * @function
 * @param {Object} state - The Redux state.
 * @returns {Array} An array of objects representing the centers of ceiling tiles, including their positions and states.
 */
const getCenters = createSelector(
  state => getAreaBoxes(state),
  state => getCurrentValue2(state, 'ceilingTiles'),
  getTileQuantities,
  state => toolIndex[state.container.currentTool].constants.ceilingTiles,
  (areaBoxes, ceilintTiles, tileQty, constants) => {
    const areaBox = areaBoxes[0];
    const { length, width, top, left } = areaBox;
    const { tile, transform: ceilingTransform, checked } = ceilintTiles;
    const { actualTileQty: actual, drawTileQty: draw, maxTileQty: max } = tileQty;

    // Set tile size depending on selection. If no tile is selected, use 2x2 for ref grid
    const hasTiles = tile !== "no_ceiling_tiles";
    const tileSize = constants[hasTiles ? tile : "2'_x_2'"];

    // calculate tileoffset for cases where part of the tile is outside the room 
    const tileOffset = mapValues({ x: length, y: width }, (v, k) => clamp((v / (2 * tileSize[k]) - (max[k] - 2) / 2), -1, 0));

    // Set the general mask pattern for each tile - this also centers the tile in the room if there are no tiles
    let masks = [{ x: tileSize.x * (tileOffset.x + .5 * hasTiles), y: tileSize.y * (tileOffset.y + .5 * hasTiles), mask: 0 }];
    if (tileSize.x > tileSize.y) masks = [{ x: tileSize.x * (tileOffset.x + .25), y: tileSize.y * (tileOffset.y + .5), mask: -1 }, { x: tileSize.x * (tileOffset.x + .75), y: tileSize.y * (tileOffset.y + .5), mask: 1 }];
    if (tileSize.x < tileSize.y) masks = [{ x: tileSize.x * (tileOffset.x + .5), y: tileSize.y * (tileOffset.y + .25), mask: -1 }, { x: tileSize.x * (tileOffset.x + .5), y: tileSize.y * (tileOffset.y + .75), mask: 1 }];

    // Adjust the tile and set the maxxed flags 
    const maxedX = max.x === draw.x;
    const maxedY = max.y === draw.y;

    const adjX = maxedX ? 1 : max.x - 2 - draw.x;
    const adjY = maxedY ? 1 : max.y - 2 - draw.y;

    let gridOffsetX = ceil(adjX / 2) + ((max.x - actual.x) % 2 === 0 ? 0 : .5);
    let gridOffsetY = ceil(adjY / 2) + ((max.y - actual.y) % 2 === 0 ? 0 : .5);

    // Final tile qty
    const qtyX = min([draw.x, max.x]) + (2 * adjX);
    const qtyY = min([draw.y, max.y]) + (2 * adjY);

    const tileCenters = [];
    for (let index = 0; index < (qtyX * qtyY); index++) {
      const idY = Math.floor(index / qtyX);
      const idX = index - idY * qtyX;

      // Actual tile index 
      const rx = idX - (maxedX ? 0 : ceil(adjX / 2) * 2);
      const ry = idY - (maxedY ? 0 : ceil(adjY / 2) * 2);

      const blocked = find(checked, { x: rx, y: ry });

      // Adjust the mask to the current position
      const adjustedMasks = masks.map((mask) => {
        const dx = mask.x + tileSize.x * (idX - gridOffsetX);
        const dy = mask.y + tileSize.y * (idY - gridOffsetY);
        return {
          ...mask,
          x: dx + ceilingTransform.x,
          y: dy + ceilingTransform.y,
          rx,
          ry,
          id: `tile_${rx}_${ry}_${mask.mask}`,
          blocked: blocked ? (blocked.mask === 0 || blocked.mask === mask.mask) : false
        }
      });
      // Filter masks that are outside the room 
      const roomFiltered = adjustedMasks.filter(mask => inRange(mask.x, left + .01, left + length) && inRange(mask.y, top + .01, top + width));
      // Filter tiles that have been blocked


      // const blocked = find(checked, { x: rx, y: ry });
      //const blockedFiltered = roomFiltered.filter(mask => !(blocked && (blocked.mask === 0 || mask.mask === blocked.mask)));
      // Add Centers 
      tileCenters.push(...roomFiltered);
    };
    // if Tiles are vertical - sort by position 
    return (tileSize.x < tileSize.y) ? sortBy(tileCenters, tile => tile.y) : tileCenters;
  }
);

/**
 * Cached selector to get transformed tile centers based on the current device and coverage.
 * @function
 * @param {Object} state - The Redux state.
 * @param {string} device - The device type ('microphones' or 'speakers').
 * @returns {Array} An array of transformed tile center coordinates.
 */
export const getTileCenters = createCachedSelector(
  getCenters,
  createCoordinateTransformer,
  state => getCurrentValue2(state, "table.transform"),
  state => getCurrentValue2(state, "ceilingTiles.tile") === "no_ceiling_tiles",
  (state, device) => getCurrentValue2(state, `${device}.coverage`).focused,

  (centers, transformer, tableTransform, noTiles, isFocused) => {
    // Calculations are only required when there's a table and it has been transformed
    const hastable = some(tableTransform);
    if (noTiles && hastable && isFocused) {
      return transformCoordinates(centers, transformer);
    } else {
      return centers;
    };
  }
)(
  (state, device) => `${device}:${getCurrentValue2(state, `${device}.coverage`)}:${getCenters}`
);


// HELPER FUNCTIONS - 
/**
 * Generates an array of jump points for microphone placement.
 * @function getJumpPoints
 * @param {number} maxMics - The maximum number of microphones that can be placed.
 * @param {boolean} [isFlat=false] - Indicates if the placement surface is flat.
 * @returns {number[]} An array of jump points for microphone quantity.
 */
const getJumpPoints = (minMics = 1, maxMics, isFlat = false) => {
  let jumpPoints;
  if (isFlat) {
    jumpPoints = Array.from({ length: maxMics }, (_, i) => i + 1);
  } else {
    jumpPoints = [1, ...Array.from({ length: Math.floor(maxMics / 2) }, (_, i) => (i + 1) * 2)]

  }

  //jumpPoints = [1, ...Array.from({ length: maxMics - 1 }, (_, i) => i + 2)]

  const trimIndex = jumpPoints.indexOf(closest(jumpPoints, minMics));
  return jumpPoints.slice(trimIndex, jumpPoints.length)
};

/**
 * Calculates microphone placement based on room parameters and microphone types.
 * @function calculateMicPlacement
 * @param {Object} area - The area object containing room and microphone properties.
 * @param {Object} area.microphones - Microphone configuration for the area.
 * @param {string} area.microphones.model - The model of the microphone.
 * @param {Object} area.microphones.coverage - Coverage settings for the microphones.
 * @param {number} area.microphones.qty - Total number of microphones (if pre-defined).
 * @param {Object} area.customized - Customization flags for the area.
 * @param {boolean} area.customized.microphones - Whether microphone placement has been customized.
 * @param {Object} area.location - Location of the area within the room.
 * @param {Object} area.dimensions - Dimensions of the area.
 * @param {Object} area.table - Table configuration (for table microphones).
 * @param {Object} micOverlaps - Object containing overlap and jump point data for microphones.
 * @param {number[]} micOverlaps.overlaps - Array of overlap values.
 * @param {number[]} micOverlaps.jumpPoints - Array of jump points for microphone quantity.
 * @param {Object} coverageBox - The coverage box dimensions.
 * @param {number} coverageRadius - The coverage radius of the microphone.
 * @param {number} scale - The scale factor for coordinate calculations.
 * @param {Object} constants - Constants used in the calculation.
 * @param {Object} constants.equipment - Equipment-specific constants.
 * @param {Object} constants.equipment.microphones - Microphone-specific constants.
 * @param {Object} constants.defOverlap - Default overlap settings.
 * @param {Object} constants.tables - Table-specific constants.
 * 
 * @returns {Object} An object containing the calculated quantity and coordinates of microphones.
 * @property {Object} qty - The quantity of microphones in x and y directions (for ceiling mics).
 * @property {Array<Object>} coords - Array of microphone coordinates and models.
 * @property {number[]} [tableJumpPoints] - Jump points for table microphones (only for table mics).
 * 
 * @description
 * This function calculates microphone placement based on the type of microphone (ceiling, table, or bar)
 * and the room configuration. It handles different scenarios:
 * - For ceiling microphones, it calculates a grid placement.
 * - For table microphones, it calculates placement along a path on the table.
 * - For bar microphones, it places a single microphone at a fixed position.
 * The function also handles customized placements and ensures minimum distance between table microphones.
 */
const calculateMicPlacement = (area, micOverlaps, coverageBox, coverageRadius, scale, constants) => {
  const { model, coverage, qty: totalMics } = area.microphones;
  const isCustomized = area.customized.microphones;
  const { location: { top, left }, dimensions: { length, width }, table } = area;
  const { overlaps, jumpPoints } = micOverlaps;
  const { isCeilingMic = false, isPendantMic = false, isTableMic = false, isBarMic = false, modelName } = constants.equipment.microphones[model.replace('mic', '_mic')];
  const { area: { table2wall }, defOverlap, tables } = constants;
  const isEndTable = tables[table.shape]?.endTable ?? false;

  let coords = [];
  let qty = { x: 1, y: 1 };
  let lineQty;
  let updatedJumpPoints = null;
  let micSpacing;

  const areaCenter = {
    x: (left + length) / 2,
    y: (top + width) / 2
  };
  const tableCenter = {
    x: table.size.length / 2,
    y: table.size.width / 2
  };




  if (isCeilingMic) {
    let micQty = totalMics > 0 ? totalMics : jumpPoints[overlaps.indexOf(closest(overlaps, defOverlap[findKey(coverage)]))];
    const ceilingMicPath = document.getElementById('micPath');
    const micPathLength = ceilingMicPath?.getTotalLength() ?? 0;
    const { width = 0, height = 0 } = ceilingMicPath?.getBBox() ?? {};
    const isFlat = width * height === 0;
    let maxMics = Math.max(1, Math.floor((micPathLength / scale) / (coverageRadius)));
    maxMics = isFlat ? Math.ceil((isEndTable ? 1 : 2) + maxMics) / 2 : maxMics;
    const minMics = Math.max(1, round((micPathLength * (isFlat ? .5 : 1)) / (2 * coverageRadius * scale) + (isFlat ? 1 : 0)));
    updatedJumpPoints = [minMics, ...Array.from({ length: maxMics - minMics }, (_, i) => i + minMics + 1)];
    lineQty = totalMics > 0 ? totalMics : (micPathLength * (isFlat ? .5 : 1)) / (1.34 * coverageRadius * scale) + (isFlat ? 1 : 0);
    lineQty = closest(updatedJumpPoints, lineQty);
    micSpacing = lineQty > 1 ? (micPathLength * (isFlat ? .5 : 1)) / (lineQty - ((isFlat || isEndTable) ? 1 : 0)) : 0;

    if (!isCustomized) {
      // Handle special table shapes cases 
      if (['round', 'keystone', 'vshape'].includes(table.shape) && coverage.focused) {
        // set the coords
        if (lineQty >= 1) {
          if (lineQty === 1) {
            coords.push({
              model,
              modelName,
              location: {
                x: areaCenter.x + (table2wall / 2),
                y: areaCenter.y
              }
            })
          } else {
            coords = Array.from({ length: lineQty }, (_, i) => {
              const point = ceilingMicPath.getPointAtLength(i * micSpacing);
              return {
                model,
                modelName,
                location: {
                  x: point.x / scale - tableCenter.x + areaCenter.x,
                  y: point.y / scale - tableCenter.y + areaCenter.y
                }
              };
            });
            if (isFlat) {
              qty = { x: lineQty, y: 1 };
            };
          }
        }
      } else {
        // calculate microphone spacing and quantity
        updatedJumpPoints = null;
        micSpacing = mapValues({ x: 0, y: 0 }, () => coverageRadius * overlaps[jumpPoints.indexOf(micQty)]);
        qty = {
          x: max([1, round(coverageBox.length / micSpacing.x)]),
          y: max([1, round(coverageBox.width / micSpacing.y)])
        };

        // Calculate the mics coordinates 
        coords = calculateCoordinates(model, modelName, coverageBox, qty, micSpacing);

        // Optimize the grid based on table shape if we have a table
        if (table && table.shape && coverage.focused && qty.x >= 2 && qty.y >= 3) {
          // Remove center mics for large tables (similar to original logic)
          const endAdj = isEndTable ? 1 : 0;
          const spliceSize = Math.max(0, qty.x - (2 - endAdj));
          for (let y = qty.y - 2; y >= 1; y--) {
            const startIndex = y * qty.x - endAdj + 1;
            coords.splice(startIndex, spliceSize);
          }
        };
      }
      if (isPendantMic) coords.forEach(device => device.location.z = height);
    };
  } else if (isTableMic) {
    const tableMicPath = document.getElementById('micPath');
    const micPathLength = tableMicPath?.getTotalLength() ?? 0;
    const { width, height } = tableMicPath.getBBox();
    const isFlat = width * height === 0;

    // Estimate the maximum number of mics 
    let maxMics = Math.max(1, Math.floor(micPathLength / scale));
    maxMics = isFlat ? Math.ceil((isEndTable ? 1 : 2) + maxMics) / 2 : maxMics;

    let minMics = (micPathLength * (isFlat ? .5 : 1)) / (2 * coverageRadius * scale) + (isFlat ? 1 : 0);
    minMics += isEndTable && !isFlat ? 1 : 0;

    // Get the gumPoints 
    updatedJumpPoints = getJumpPoints(minMics, maxMics, isFlat || isEndTable);
    // Estimate ideal starting number 
    let micQty = totalMics > 0 ? totalMics : (micPathLength * (isFlat ? .5 : 1)) / (1.34 * coverageRadius * scale) + (isFlat ? 1 : 0);
    micQty = closest(updatedJumpPoints, micQty);

    // Calculagte microphone spacing
    micSpacing = micQty > 1 ? (micPathLength * (isFlat ? .5 : 1)) / (micQty - ((isFlat || isEndTable) ? 1 : 0)) : 0;

    if (micPathLength >= 0) {
      if (micQty === 1) {
        const halfWayPoint = tableMicPath.getPointAtLength(micPathLength / 2);
        const middlePoint = {
          x: halfWayPoint.x / scale - tableCenter.x + areaCenter.x,
          y: halfWayPoint.y / scale - tableCenter.y + areaCenter.y
        }
        coords.push({
          model,
          modelName,
          location: {
            x: isFlat ? areaCenter.x : middlePoint.x,
            y: isFlat ? areaCenter.y : middlePoint.y
          }
        })
      } else {
        coords = Array.from({ length: micQty }, (_, i) => {
          const point = tableMicPath.getPointAtLength(i * micSpacing);
          return {
            model,
            modelName,
            location: {
              x: point.x / scale - tableCenter.x + areaCenter.x,
              y: point.y / scale - tableCenter.y + areaCenter.y
            }
          };
        });
      };
    };

    // Merge close mics, TTM mics can't be closer than 1 meter
    coords.forEach((elem, index, array) => {
      const other = array.find(test =>
        Math.abs(test.location.x - elem.location.x) < 1 &&
        Math.abs(test.location.y - elem.location.y) < 1 &&
        test !== elem
      );

      if (other) {
        if (Math.abs(elem.location.x - other.location.x) <= 0.001) {
          elem.location.x = other.location.x = (elem.location.x + other.location.x) / 2;
        };
        if (Math.abs(elem.location.y - other.location.y) <= 0.001) {
          elem.location.y = other.location.y = (elem.location.y + other.location.y) / 2;
        };
      };
    });

    // Clean up duplicates
    coords = coords.filter((elem, index, array) =>
      index === array.findIndex(test =>
        Math.abs(test.location.x - elem.location.x) <= 0.001 && Math.abs(test.location.y - elem.location.y) <= 0.001
      )
    );

    qty = coords.length;
  } else if (isBarMic) {
    if (!isCustomized) {
      coords = [
        {
          model,
          modelName,
          location: {
            x: left + .134,
            y: top + width / 2
          }
        }];
    };
  };
  return { qty, coords, updatedJumpPoints };
};

/**
* @function calculateMicPlotData
* @description Calculates distance-based plot data for microphone coverage visualization.
* 
* @param {string} id - Area identifier
* @param {Array} [layout=[]] - Array of microphone positions
* @param {Object} grid - Grid parameters for calculations
* @param {number} grid.gridSpacing - Distance between grid points
* @param {number} grid.gridXOffset - X-axis grid offset
* @param {number} grid.gridYOffset - Y-axis grid offset
* @param {number} grid.gridX - Number of grid points in X direction
* @param {number} grid.gridY - Number of grid points in Y direction
* 
* @returns {Object|undefined} Plot data for heatmap visualization
* @property {string} id - Area identifier
* @property {number} receiverSpacing - Grid spacing used for calculations
* @property {number} receiverXOffset - X offset of the grid
* @property {number} receiverYOffset - Y offset of the grid
* @property {Array<Array<number>>} mapArray - 2D array of distances to nearest microphone
* 
* @description
* For each grid point:
* 1. Calculates distance to each microphone
* 2. Takes minimum distance (closest microphone)
* 3. Builds 2D array of distances for heatmap
* 
* @example
* const plotData = calculateMicPlotData(
*   'area1',
*   [{ location: { x: 10, y: 20 } }],
*   { gridSpacing: 1, gridXOffset: 0, gridYOffset: 0, gridX: 20, gridY: 20 }
* );
* 
* @note
* - Returns undefined if layout is empty
* - Uses Euclidean distance calculation
* - Maintains grid parameters in return data for visualization
*/
const calculateMicPlotData = (id, layout = [], grid) => {
  const { gridSpacing, gridXOffset, gridYOffset, gridX, gridY } = grid;
  const mapArray = [];
  if (layout.length === 0) return;
  for (let y = 0; y <= gridY; y++) {
    const lineArray = [];
    for (let x = 0; x <= gridX; x++) {
      const point = {
        x: gridXOffset + x * gridSpacing,
        y: gridYOffset + y * gridSpacing,
      };
      const distanceAtPoint = layout.reduce((minDist, mic) => {
        const dx = mic.location.x - point.x;
        const dy = mic.location.y - point.y;
        const dist = Math.hypot(dx, dy);
        return Math.min(minDist, dist)
      }, Infinity);
      lineArray.push(distanceAtPoint);
    };
    mapArray.push(lineArray);
  };
  const data = {
    id: `${id} full`,
    receiverSpacing: gridSpacing,
    receiverXOffset: gridXOffset,
    receiverYOffset: gridYOffset,
    mapArray,
  };
  return data;
};

/**
 * Calculates loudspeaker placement based on room parameters and constants.
 * @function
 * @param {Object} area - The area object containing room and speaker properties.
 * @param {number} spkrOverlap - The overlap value for speakers.
 * @param {Object} coverageBox - The coverage box dimensions.
 * @param {number} coverageRadius - The coverage radius of the speaker.
 * @param {Object} micQty - The quantity of microphones.
 * @param {Object} constants - Constants used in the calculation.
 * @returns {Array} An array of calculated speaker coordinates.
 */
const calculateSpeakerPlacement = (area, spkrOverlap, coverageBox, coverageRadius, micQty, scale, constants) => {
  const { location: { top, left }, dimensions: { length, width }, table: { shape, size }, speakers: { model, coverage }, customized: { speakers: isSpeakerCustom } } = area;
  // const { model, coverage } = area.speakers;
  // const { speakers: isSpeakerCustom } = customized;
  // const { table: { shape, size } } = area;
  const { isCeilingSpkr = false, isBarSpkr = false, modelName } = constants.equipment.speakers[model];
  const { tables } = constants;
  const isEndTable = tables[shape]?.endTable ?? false;
  let spacing = mapValues({ x: 0, y: 0 }, () => coverageRadius * spkrOverlap);

  /**
   * Calculates the initial quantity of speakers based on coverage box dimensions and spacing.
   * @function
   * @inner
   * @returns {Object} An object with x and y properties representing the number of speakers in each direction.
   */
  const getSpeakerQuantity = () => {
    const box = mapKeys(pick(coverageBox, ['length', 'width']), (_, k) => k === 'length' ? 'x' : 'y');
    return mapValues(box, (dim, key) => {
      let count = max([1, round(dim / spacing[key]) - coverageBox.spkrAdj]);
      if (count === 1) return 1;
      const edgeToFirstSpeaker = dim / 2 - count / 2 * spacing[key] + spacing[key] / 2;
      return edgeToFirstSpeaker >= spacing[key] / 2 ? count : count - 1;
    });
  };

  /**
   * Adjusts the speaker quantity and spacing based on microphone quantity and room characteristics.
   * @function
   * @inner
   * @param {Object} spkrQty - The initial speaker quantity object with x and y properties.
   * @returns {Object} An object containing the adjusted quantity and spacing for speakers.
   * @property {Object} qty - The adjusted quantity of speakers with x and y properties.
   * @property {Object} spacing - The adjusted spacing between speakers with x and y properties.
   */
  const adjustSpeakerQuantity = (spkrQty) => {
    const { x: xMics, y: yMics } = micQty;
    let { x: xSpkrs, y: ySpkrs } = spkrQty;

    // Determine if table is in portrait orientation (taller than wide)
    const isPortrait = coverageBox.width > coverageBox.length;

    // Helper to adjust spacing after changing speaker count
    const updateSpacing = (dimension) => {
      if (shape && findKey(coverage) === "focused") {
        spacing[dimension] = coverageBox[dimension === 'x' ? 'length' : 'width'] / (dimension === 'x' ? xSpkrs : ySpkrs);
      }
    };

    if (xMics * yMics === 1) {
      if (xSpkrs === 1 && ySpkrs === 1) {
        if (isPortrait) {
          ySpkrs += 1;
          updateSpacing('x');
        } else {
          xSpkrs += 1;
          updateSpacing('y');
        }
      }
    }

    // For two microphone case
    else if (xMics * yMics === 2) {
      const spkrCount = xSpkrs * ySpkrs;
      if (spkrCount === 2) {
        if (isPortrait) {
          xSpkrs += 1;
          updateSpacing('y');
        } else {
          ySpkrs += 1;
          updateSpacing('x');
        }
      }
      else if (spkrCount === 3) {
        if (isPortrait) {
          ySpkrs -= 1;
          xSpkrs += 1;
          updateSpacing('y');
        } else {
          xSpkrs -= 1;
          ySpkrs += 1;
          updateSpacing('x');
        }
      }
    }

    // For all other cases
    else {
      if (!isPortrait && yMics * ySpkrs === 1) { // yMics * ySpkrs === 1
        ySpkrs += 1;
        if (xSpkrs > xMics) {
          xSpkrs -= 1;
        };
        updateSpacing('x');
      } else if (isPortrait && xMics * xSpkrs === 1) { // if (xMics * xSpkrs === 1)
        xSpkrs += 1;
        if (ySpkrs > yMics) {
          ySpkrs -= 1;
        };
        updateSpacing('y');
      };
    };

    return {
      qty: { x: xSpkrs, y: ySpkrs },
      spacing
    };
  };

  const areaCenter = {
    x: (left + length) / 2,
    y: (top + width) / 2
  };
  const tableCenter = {
    x: size.length / 2,
    y: size.width / 2
  }

  let coords = [];
  if (isCeilingSpkr) {
    if (!isSpeakerCustom) {
      const initialQty = getSpeakerQuantity();
      const { qty: adjustedQty, spacing: adjustedSpacing } = adjustSpeakerQuantity(initialQty);
      const spkrPath = document.getElementById('spkrPath');
      const spkrLength = spkrPath?.getTotalLength() - (isEndTable ? coverageRadius * scale : 0) ?? 0;
      const startingPoint = isEndTable ? coverageRadius * scale / 2 : 0;
      const { width = 0, height = 0 } = spkrPath?.getBoundingClientRect() ?? {};
      const isFlat = width * height === 0;

      switch (true) {
        case (shape === 'round' && coverage.focused):
          if (adjustedQty.x * adjustedQty.y === 1) {
            coords.push({
              model,
              modelName,
              location: {
                x: areaCenter.x,
                y: areaCenter.y
              }
            })
          } else {
            let qty = Math.floor(spkrLength / (spacing.x * scale))
            qty = qty % 2 === 0 ? qty : qty + 1
            const space = spkrLength / qty
            coords = Array.from({ length: qty }, (_, i) => {
              const point = spkrPath.getPointAtLength(i * space);
              return {
                model,
                modelName,
                location: {
                  x: point.x / scale - tableCenter.x + areaCenter.x,
                  y: point.y / scale - tableCenter.y + areaCenter.y
                }
              }

            })
          }
          break;
        case (['keystone', 'vshape'].includes(shape) && coverage.focused && size.width >= spacing.y):
          if (initialQty.x * initialQty.y === 1) {
            coords.push({
              model,
              modelName,
              location: {
                x: areaCenter.x,
                y: areaCenter.y
              }
            })
          } else {
            let qty = Math.floor(spkrLength / (spacing.x * scale))
            const space = (spkrLength * (isFlat ? .5 : 1)) / (qty - ((isFlat || isEndTable) ? 1 : 0));
            coords = Array.from({ length: qty }, (_, i) => {
              const point = spkrPath.getPointAtLength(startingPoint + i * space);
              return {
                model,
                modelName,
                location: {
                  x: point.x / scale - tableCenter.x + areaCenter.x,
                  y: point.y / scale - tableCenter.y + areaCenter.y
                }
              }
            })
          }
          break;

        default:
          coords = calculateCoordinates(model, modelName, coverageBox, adjustedQty, adjustedSpacing);
          // Optimize the grid based on table shape if we have a table
          if (shape && coverage.focused && adjustedQty.x >= 2 && adjustedQty.y >= 3) {
            // Remove center spkrs for large tables (similar to original logic)
            const endAdj = isEndTable ? 1 : 0;
            const spliceSize = Math.max(0, adjustedQty.x - (2 - endAdj));
            for (let y = adjustedQty.y - 2; y >= 1; y--) {
              const startIndex = y * adjustedQty.x - endAdj + 1;
              coords.splice(startIndex, spliceSize);
            }
          };

      };
    };
  } else if (isBarSpkr) {
    if (!isSpeakerCustom) {
      const micModel = area.microphones.model;
      const modelName = constants.equipment.microphones[micModel]?.modelName;
      coords = [
        {
          model,
          modelName,
          location: {
            x: left + .134,
            y: top + width / 2
          }
        }];
    };
  }
  return coords
};

/**
* @function calculagteCeilingRelativePloptData
* @description Calculates relative SPL plot data for ceiling-mounted speakers.
* 
* @param {Object} area - Area information and dimensions
* @param {Array} [layout=[]] - Array of speaker positions
* @param {Object} grid - Grid parameters for calculations
* @param {Object} constants - System constants including speaker properties
* 
* @returns {Object|undefined} Plot data for relative SPL visualization
* @property {string} id - Area identifier
* @property {number} receiverSpacing - Grid spacing
* @property {number} receiverXOffset - X offset of grid
* @property {number} receiverYOffset - Y offset of grid
* @property {Array<Array<number>>} mapArray - 2D array of adjusted SPL values
* @property {number} averageTarget - Target average SPL level
* 
* @description
* Calculation process:
* 1. Defines average box based on speaker coverage radius
* 2. For each grid point:
*    - Calculates distance to each speaker
*    - Applies polar pattern attenuation
*    - Computes distance attenuation
*    - Combines into total SPL
* 3. Post-processes SPL values:
*    - Calculates average target level
*    - Applies non-linear scaling above target + 3dB
* 
* SPL Calculation Components:
* - Direct sound level based on sensitivity
* - Distance attenuation (-20*log10(distance))
* - Angular attenuation from polar response
* - Combined using logarithmic addition
* 
* @example
* const plotData = calculagteCeilingRelativePloptData(
*   {
*     id: 'area1',
*     speakers: { model: 'SP100', height: 3 },
*     location: { top: 0, left: 0 },
*     dimensions: { length: 20, width: 15 }
*   },
*   [{ location: { x: 10, y: 7.5 } }],
*   { gridSpacing: 0.5, ... },
*   constants
* );
* 
* @note
* - Returns undefined if layout is empty
* - Uses 5-degree steps for polar pattern interpolation
* - Applies special scaling to levels above target + 3dB
* - Average calculated only within coverage radius of speakers
*/
const calculagteCeilingRelativePloptData = (area, layout = [], grid, constants) => {
  const { gridSpacing, gridXOffset, gridYOffset, gridX, gridY } = grid;
  const { speakers: { model, height }, location: { top, left }, dimensions: { length, width } } = area;
  if (layout.length === 0) return;
  const { equipment: { speakers: { [model]: { coverageAngle, sensitivity, polarData } } }, area: { talkerHeight } } = constants;

  // Calculate the average box - this is the area that will be considered to calculate average to keep variation constrained 
  const coverageRadius = (height - talkerHeight * Math.tan(coverageAngle * Math.PI / 180));
  const averageBox = {
    x0: Math.max(left, (layout.reduce((min, speaker) => Math.min(min, speaker.location.x), layout[0].location.x)) - coverageRadius) - left,
    y0: Math.max(top, (layout.reduce((min, speaker) => Math.min(min, speaker.location.y), layout[0].location.y)) - coverageRadius) - top,
    x1: Math.min((layout.reduce((max, speaker) => Math.max(max, speaker.location.x), layout[0].location.x)) + coverageRadius, length - left) - left,
    y1: Math.min((layout.reduce((max, speaker) => Math.max(max, speaker.location.y), layout[0].location.y)) + coverageRadius, width - top) - top
  };

  let mapArray = [];
  let runningTotal = 0, runningCount = 0;
  let minSPL = Infinity, maxSPL = 0;
  for (let y = 0; y <= gridY; y++) {
    const lineArray = []
    for (let x = 0; x <= gridX; x++) {
      const point = {
        x: gridXOffset + x * gridSpacing,
        y: gridYOffset + y * gridSpacing,
      };
      const pointSum = layout.reduce((sum, spkr) => {
        const dx = spkr.location.x - point.x;
        const dy = spkr.location.y - point.y;
        const dist = Math.hypot(dx, dy, height);
        const angle = Math.acos(height / dist) * (180 / Math.PI);
        // Directly calculate the enclosing angles in 5-degree steps
        const lowerAngle = floor(angle / 5) * 5;
        const upperAngle = ceil(angle / 5) * 5;
        // Calculate the polar attenuation 
        const interpPercentage = angle === 0 ? 1 : (angle - lowerAngle) / (upperAngle - lowerAngle);
        const angleAttenuation = polarData[lowerAngle] + (polarData[upperAngle] - polarData[lowerAngle]) * interpPercentage;
        // Calculate distance attenuation
        const distanceAttenuation = -20 * Math.log10(dist);
        // Add the resulting level at distance 
        const pointLevel = Math.pow(10, (sensitivity + distanceAttenuation + angleAttenuation) * .1);
        return sum + pointLevel;
      }, 0);
      const pointTotal = 10 * Math.log10(pointSum)
      lineArray.push(pointTotal);


      // Sum values only if they're inside the averageBox
      if (x >= averageBox.x0 && x <= averageBox.x1 && y >= averageBox.y0 && y <= averageBox.y1) {
        runningTotal += pointSum;
        runningCount++;
        minSPL = Math.min(minSPL, pointTotal);
        maxSPL = Math.max(maxSPL, pointTotal);
      };
    };

    mapArray.push(lineArray);
  };
  const averageTarget = 10 * Math.log10(runningTotal / runningCount);

  const yellowLevel = averageTarget + 3;
  const factor = Math.sqrt((3.5 - 3) / Math.pow(maxSPL - yellowLevel, 2));
  mapArray = mapArray.map(line => {
    return line.map(point => {
      if (point >= yellowLevel) {
        const aboveYellow = point - yellowLevel;
        const scaledValue = Math.pow(aboveYellow * factor, 2) + 3;
        return averageTarget + scaledValue;
      } else {
        return point
      }
    })
  })


  const data = {
    id: `${area.id} full`,
    receiverSpacing: gridSpacing,
    receiverXOffset: gridXOffset,
    receiverYOffset: gridYOffset,
    mapArray,
    averageTarget
  };
  return data
};

/**
* @function calculateBarRelativePlotData
* @description Calculates relative SPL plot data for wall-mounted (bar) speakers.
* 
* @param {Object} area - Area information including speaker model and acoustics
* @param {Array} [layout=[]] - Array of speaker positions
* @param {Object} grid - Grid parameters for calculations
* @param {Function} deviceAdjutment - Function to adjust device positions and calculate aim points
* @param {Object} constants - System constants including speaker properties
* 
* @returns {Object|undefined} Plot data for relative SPL visualization
* @property {string} id - Area identifier
* @property {number} receiverSpacing - Grid spacing (typically 0.1m for bar speakers)
* @property {number} receiverXOffset - X offset of grid
* @property {number} receiverYOffset - Y offset of grid
* @property {Array<Array<number>>} mapArray - 2D array of adjusted SPL values
* @property {number} averageTarget - Target SPL level based on acoustics
* 
* @description
* Calculation process:
* 1. Adjusts speaker positions and calculates aim points
* 2. For each grid point:
*    - Calculates distance to each speaker
*    - Computes angle relative to speaker aim
*    - Applies polar pattern attenuation
*    - Calculates distance attenuation
*    - Combines into total SPL
* 3. Post-processes SPL values:
*    - Uses acoustics-based target level
*    - Applies non-linear scaling above target + 3dB
* 
* SPL Calculation Components:
* - Direct sound level based on sensitivity
* - Distance attenuation (-20*log10(distance))
* - Angular attenuation from polar response
* - Combined using logarithmic addition
* 
* @example
* const plotData = calculateBarRelativePlotData(
*   {
*     id: 'area1',
*     speakers: { model: 'BarSP100' },
*     acoustics: 'fair'
*   },
*   [{ location: { x: 0, y: 7.5 } }],
*   { gridSpacing: 0.1, ... },
*   deviceAdjustment,
*   constants
* );
* 
* @note
* - Returns undefined if layout is empty
* - Uses finer grid spacing (0.1m) than ceiling speakers
* - Angles calculated relative to speaker aim point
* - Includes max SPL limiting at sensitivity level
* - Uses acoustic environment for target level
*/
const calculateBarRelativePlotData = (area, layout = [], grid, deviceAdjutment, constants) => {
  const { gridSpacing, gridXOffset, gridYOffset, gridX, gridY } = grid;
  const { speakers: { model }, acoustics: currentAcoustics } = area
  if (layout.length === 0) return;
  const { equipment: { speakers: { [model]: { sensitivity, polarData, targetSPL = [] } } }, acoustics } = constants;
  const barLayout = layout.map(spkr => deviceAdjutment('speakers', spkr));
  let mapArray = [];
  let minSPL = Infinity, maxSPL = 0;

  for (let y = 0; y <= gridY; y++) {
    const lineArray = [];
    for (let x = 0; x <= gridX; x++) {
      const point = {
        x: gridXOffset + x * gridSpacing,
        y: gridYOffset + y * gridSpacing,
      };
      const pointSum = barLayout.reduce((sum, spkr) => {
        const dx = spkr.location.x - point.x;
        const dy = spkr.location.y - point.y;
        const dist = Math.hypot(dx, dy);
        const angle = clamp(findAngle(spkr.aimPoint, spkr.location, point), 0, 90);
        // Directly calculate the enclosing angles in 5-degree steps
        const lowerAngle = floor(angle / 5) * 5;
        const upperAngle = ceil(angle / 5) * 5;
        // Calculate the polar attenuation 
        let angleAttenuation
        if (lowerAngle === upperAngle) {
          angleAttenuation = polarData[lowerAngle]
        } else {
          const interpPercentage = (angle - lowerAngle) / (upperAngle - lowerAngle);
          angleAttenuation = polarData[lowerAngle] + (polarData[upperAngle] - polarData[lowerAngle]) * interpPercentage;
        }
        // Calculate distance attenuation
        const distanceAttenuation = -20 * Math.log10(dist);
        // Add the resulting level at distance 
        const pointLevel = Math.pow(10, Math.min(sensitivity, (sensitivity + distanceAttenuation + angleAttenuation)) * .1);
        return sum + pointLevel;
      }, 0);
      const pointTotal = 10 * Math.log10(pointSum);
      lineArray.push(pointTotal);
      minSPL = Math.min(minSPL, pointTotal);
      maxSPL = Math.max(maxSPL, pointTotal);
    };
    mapArray.push(lineArray);
  };
  const averageTarget = targetSPL[indexOf(acoustics, currentAcoustics)];
  const yellowLevel = averageTarget + 3;
  const factor = Math.sqrt((3.5 - 3) / Math.pow(maxSPL - yellowLevel, 2));

  mapArray = mapArray.map(line => {
    return line.map(point => {
      if (point >= yellowLevel) {
        const aboveYellow = point - yellowLevel;
        const scaledValue = Math.pow(aboveYellow * factor, 2) + 3;
        return averageTarget + scaledValue;
      } else {
        return point
      }
    })
  })

  const data = {
    id: `${area.id} full`,
    receiverSpacing: gridSpacing,
    receiverXOffset: gridXOffset,
    receiverYOffset: gridYOffset,
    mapArray,
    averageTarget
  };
  return data
};

/**
 * Calculates camera placement based on room parameters and constants.
 * @function
 * @param {Object} area - The area object containing room and microphone properties.
 * @param {Object} constants - Constants used in the calculation.
 * @returns {Object} An object containing the calculated quantity and coordinates of microphones.
 */
const calculateCameraPlacement = (area, constants) => {
  const { model, hasCamera } = area.cameras;
  const isCameraCustom = area.customized.cameras;
  const { location: { top, left }, dimensions: { width } } = area;
  let coords = [];
  if (hasCamera && !isCameraCustom) {
    const { modelName, coverageAngle, coverageRadius } = constants.equipment.cameras[model];
    coords = [
      {
        model,
        modelName,
        location: {
          x: left + .134,
          y: top + width / 2
        },
        coverageAngle,
        coverageRadius
      }]
  } else {
    coords = []
  };
  return coords;
};

/**
* @function calculateCameraPlotData
* @description Calculates distance-based plot data for camera coverage visualization.
* 
* @param {string} id - Area identifier
* @param {Array} [layout=[]] - Array of camera positions
* @param {Object} grid - Grid parameters for calculations
* @param {number} grid.gridSpacing - Distance between grid points
* @param {number} grid.gridXOffset - X-axis grid offset
* @param {number} grid.gridYOffset - Y-axis grid offset
* @param {number} grid.gridX - Number of grid points in X direction
* @param {number} grid.gridY - Number of grid points in Y direction
* @param {Function} deviceAdjustment - Function to adjust camera positions and calculate aim points
* 
* @returns {Object|undefined} Plot data for camera coverage visualization
* @property {string} id - Area identifier
* @property {number} receiverSpacing - Grid spacing used for calculations
* @property {number} receiverXOffset - X offset of the grid
* @property {number} receiverYOffset - Y offset of the grid
* @property {Array<Array<number>>} mapArray - 2D array of distances to nearest camera
* @property {Object} camera - Adjusted camera data including position and aim point
* 
* @description
* For each grid point:
* 1. Adjusts camera positions for plotting (render mode)
* 2. Calculates distance to each camera
* 3. Takes minimum distance (closest camera)
* 4. Includes camera aim data for visualization
* 
* @example
* const plotData = calculateCameraPlotData(
*   'area1',
*   [{ location: { x: 0, y: 10 } }],
*   { gridSpacing: 1, gridXOffset: 0, gridYOffset: 0, gridX: 20, gridY: 20 },
*   deviceAdjustment
* );
* 
* @note
* - Returns undefined if layout is empty
* - Uses Euclidean distance calculation
* - Includes render mode for device adjustment
* - Currently supports single camera (uses first camera's data)
* - Commented code suggests planned support for multiple areas
*/
const calculateCameraPlotData = (id, layout = [], grid, deviceAdjustment) => {
  const { gridSpacing, gridXOffset, gridYOffset, gridX, gridY } = grid;
  // const mergedMap = new Map();
  const mapArray = [];
  // areas.forEach(area => {
  if (layout.length === 0) return;
  const cameraLayout = layout.map(camera => ({ ...camera, ...deviceAdjustment('cameras', camera, true) }));
  for (let y = 0; y <= gridY; y++) {
    const lineArray = [];
    for (let x = 0; x <= gridX; x++) {
      const point = {
        x: gridXOffset + x * gridSpacing,
        y: gridYOffset + y * gridSpacing,
      };
      const distanceAtPoint = cameraLayout.reduce((minDist, mic) => {
        const dx = mic.location.x - point.x;
        const dy = mic.location.y - point.y;
        const dist = Math.hypot(dx, dy);
        return Math.min(minDist, dist)
      }, Infinity);
      lineArray.push(distanceAtPoint);
    };
    mapArray.push(lineArray);
  };
  const data = {
    id: `${id} full`,
    receiverSpacing: gridSpacing,
    receiverXOffset: gridXOffset,
    receiverYOffset: gridYOffset,
    mapArray,
    camera: cameraLayout[0]
  };
  return data;
};


/**
 * Transforms an array of coordinates using a transformer function.
 * @function
 * @param {Array} coordinates - The array of coordinates to transform.
 * @param {Function} transformer - The function to use for transformation.
 * @param {string} [coordProperty=null] - The property name of the coordinate object, if applicable.
 * @returns {Array} The array of transformed coordinates.
 */
const transformCoordinates = (coordinates, transformer, coordProperty = null) => {
  return coordinates.map(item => {
    if (coordProperty && typeof item === 'object' && item.hasOwnProperty(coordProperty)) {
      return {
        ...item,
        [coordProperty]: transformer(item[coordProperty])
      };
    } else if (typeof item === 'object' && 'x' in item && 'y' in item) {
      return {
        ...item,
        ...transformer(item)
      };
    } else {
      return item; // Return unchanged if it doesn't match expected structure
    }
  });
};

/**
 * Finds the closest ceiling tile to a given set of coordinates.
 * @function
 * @param {Array} centers - An array of tile center coordinates.
 * @param {Object} coordinates - The x and y coordinates to find the closest tile to.
 * @returns {Object|undefined} The closest tile object or undefined if no suitable tile is found.
 */
const findClosestTile = (centers, { x, y }) => {
  // Get the grid size
  const qtyX = centers.filter(center => center.y === centers[0].y).length;
  const qtyY = centers.filter(center => center.x === centers[0].x).length;

  // Find the closest tile
  const tile = minBy(centers, center => Math.hypot((center.x - x), (center.y - y)));

  // get the index and tile id
  const index = centers.indexOf(tile);
  const idY = Math.floor(index / qtyX);
  const idX = index - idY * qtyX;

  /**
   * Calculates the center point of a set of tile centers and adds it to the array.
   * This is particularly useful for rectangular tiles.
   * @function
   * @inner
   * @param {Array} tileCenters - An array of tile center objects, each with x and y coordinates.
   * @returns {Array} The input array with an additional center point added.
   */
  const getCenter = (tileCenters) => {
    const sumX = tileCenters.reduce((sum, coord) => sum + coord.x, 0);
    const sumY = tileCenters.reduce((sum, coord) => sum + coord.y, 0);
    const middleX = sumX / tileCenters.length;
    const middleY = sumY / tileCenters.length;
    tileCenters.push({ x: middleX, y: middleY, blocked: tileCenters.some(tile => tile.blocked) });
    return tileCenters
  };

  // If tile is blocked, find closest tile around 
  if (tile.blocked) {
    // calculate the offset between mic and tile center 
    const offsetX = x - tile?.x;
    const offsetY = y - tile?.y;

    // Determine which surrounding tiles to check, 0.24384 is 40% of the mask size (i.e. .4 * 0.6096). Thi determines what adjacent tiles to consider
    const xAdj = Math.abs(offsetX) > .24384 ? (offsetX > 0 ? [0, 1] : [-1, 0]) : [-1, 1];
    const yAdj = Math.abs(offsetY) > .24384 ? (offsetY > 0 ? [0, 1] : [-1, 0]) : [-1, 1];

    // Create array of tiles to check
    const tilesToCheck = [];
    for (let y = max([idY + yAdj[0], 0]); y <= min([idY + yAdj[1], qtyY - 1]); y++) {
      for (let x = max([idX + xAdj[0], 0]); x <= min([idX + xAdj[1], qtyX - 1]); x++) {
        const index = y * qtyX + x
        if (!centers[index].blocked) tilesToCheck.push(centers[index]);
      };
    };

    // If there are no tiles to check, there's no solution, return 
    if (tilesToCheck.length === 0) return

    // find the closest tile in tilesToCheck that is not blocked
    let targetTile = minBy(tilesToCheck.filter(tile => !tile.blocked), tile => Math.hypot((tile.x - x), (tile.y - y)));

    // Check how many centers does the tile have. This is to refine position in rectangular tiles
    let tileCenters = tilesToCheck.filter(tile => tile.rx === targetTile.rx && tile.ry === targetTile.ry && !tile.blocked);

    // If there are more than one center it implies is  a rectangular tile. Add the center point and check which is closest
    if (tileCenters.length === 1) {
      return tileCenters.pop();
    } else if (tileCenters.length > 1) {
      tileCenters = getCenter(tileCenters)
      return minBy(tileCenters.filter(tile => !tile.blocked), tile => Math.hypot((tile.x - x), (tile.y - y)))
    };
  };
  // If the tile is not blocked check correct center if tile is rectangular
  if (tile.mask === 0) {
    return tile;
  } else {
    const closest = centers.filter(center => trimEnd(center.id, "_-10") === trimEnd(tile.id, "_-10"));
    const closeCenters = getCenter(closest)
    return minBy(closeCenters.filter(tile => !tile.blocked), tile => Math.hypot((tile.x - x), (tile.y - y)))
  };
};

/**
 * Blocks tiles around a used tile in the ceiling grid.
 * @function
 * @param {Array} centers - An array of tile center coordinates.
 * @param {Object} tile - The tile object around which to block other tiles.
 * @returns {Array} The updated array of tile centers with blocked tiles marked.
 */
const blockTiles = (centers, tile) => {
  // Get the grid size
  const qtyX = centers.filter(center => center.y === centers[0].y).length;

  /**
   * Blocks tiles around a given base index in the centers array.
   * @function
   * @inner
   * @param {number} baseIndex - The index of the central tile to block around.
   * @param {boolean} [isHorizontal=true] - Whether to block horizontally adjacent tiles.
   * @param {boolean} [isVertical=true] - Whether to block vertically adjacent tiles.
   */
  const block = (baseIndex, isHorizontal = true, isVertical = true) => [[0, 0], [-1, 0], [1, 0], [0, -1], [0, 1]].map(([x, y]) => {
    const index = baseIndex + (isVertical ? x : 0) + (qtyX * (isHorizontal ? y : 0));
    if (inRange(index, 0, centers.length)) centers[index].blocked = true;
    return null
  });

  // If the tile has an id, get the index and block adjacents
  // If not, find the ids of tile and block
  if (tile.id) {
    const baseIndex = centers.indexOf(tile);
    block(baseIndex);
  } else {
    const closest = minBy(centers, center => Math.hypot((center.x - tile.x), (center.y - tile.y)));
    const closeSet = centers.filter(tile => trimEnd(tile.id, "_-10") === trimEnd(closest.id, "_-10"));
    const indices = closeSet.map(tile => centers.indexOf(tile));
    const isHorizontal = Math.abs(indices[0] - indices[1]) === 1;
    indices.map(baseIndex => block(baseIndex, isHorizontal, !isHorizontal));
  };
  return centers
};













